import React from 'react'
import type { PivotDateRange, PivotLayout, PivotLayoutField, PivotLayoutValueFilter } from '@ztechapps/common.contract'
import type { PivotFilterChip } from '../pivot/listPivotFilterChips'
import type { PivotData } from '../pivot/types'
import { BusyButton } from '@ztechapps/webui.button'
import { HorizontalStack } from '@ztechapps/webui.util'
import { PivotFilterPopover } from '../FieldPanel/PivotFilterPopover'
import { PivotTopNFilter } from '../FieldPanel/PivotTopNFilter'
import { PIVOT_FILTER_ALL_TEXT } from '../pivot/constants'
import { listPivotValueFilterChips } from '../pivot/listPivotValueFilterChips'

/** Props for the pivot filter bar. */
export type PivotFilterBarProps = {
  chips: PivotFilterChip[]
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures' | 'filters'>
  data: PivotData
  timeZone: string
  onFilter: (field: PivotLayoutField, excludedValues: string[], dateRange?: PivotDateRange) => void
  onValueFilter: (field: PivotLayoutField, valueFilter: PivotLayoutValueFilter | null) => void
}

/**
 * Filter-only fields as chips, each opening its member picker — a field the grid shows has its picker in the grid
 * header. A Rows or Columns field with a Top / Bottom N set has its chip here too, reopening that popover.
 */
export const PivotFilterBar = ({ chips, layout, data, timeZone, onFilter, onValueFilter }: PivotFilterBarProps) => {
  return (
    // grows into the space the toolbar's right cluster leaves, so the chips and the toolbar share one row
    <HorizontalStack gap={2} className="flex-wrap flex-grow-1">
      {chips.map(({ filter, label, text }) => (
        <PivotFilterPopover
          key={`${filter.field}|${filter.dateGroup ?? ''}`}
          data={data}
          field={filter}
          filter={filter}
          timeZone={timeZone}
          onSave={(excludedValues, dateRange) => onFilter(filter, excludedValues, dateRange)}
          trigger={({ ref, onClick, 'aria-expanded': ariaExpanded }) => (
            <BusyButton
              variant="outline-secondary"
              bsSize="sm"
              icon="filter"
              text={label}
              active={text !== PIVOT_FILTER_ALL_TEXT}
              aria-expanded={ariaExpanded}
              ref={ref}
              onClick={onClick}
            >
              <span className="fw-semibold">{text}</span>
            </BusyButton>
          )}
        />
      ))}
      {listPivotValueFilterChips(layout).map(({ filter, label }) => (
        <PivotTopNFilter
          key={`${filter.field}|${filter.dateGroup ?? ''}|valueFilter`}
          data={data}
          field={filter}
          layout={layout}
          onApply={(valueFilter) => onValueFilter(filter, valueFilter)}
          trigger={({ onClick, 'aria-expanded': ariaExpanded }) => (
            <BusyButton
              variant="outline-secondary"
              bsSize="sm"
              icon="filter"
              text={label}
              active
              aria-expanded={ariaExpanded}
              onClick={onClick}
            />
          )}
        />
      ))}
    </HorizontalStack>
  )
}
